// Enrollment UI for real TOTP-based 2FA (authenticator apps like Google
// Authenticator/Authy) — the counterpart to the backend's twoFactor()
// plugin in auth-config.ts. Rendered full-screen by <TwoFactorGate> when a
// Super Admin / Executive Admin user hasn't set up 2FA yet (see
// /api/rbac/check-session's twoFARequired/twoFAEnabled fields).
//
// Flow, matching better-auth's twoFactor plugin exactly (verified end-to-end
// against a real local wrangler dev instance before this UI was written):
//   1. enable({password}) -> returns {totpURI, backupCodes}. This does NOT
//      immediately mark 2FA active (skipVerificationOnEnable defaults to
//      false) — it only creates the secret.
//   2. User scans the QR code (rendered from totpURI via the already-
//      installed `qrcode` package) and enters the 6-digit code it produces.
//   3. verifyTotp({code}) while still holding this normal session is what
//      actually marks twoFactorEnabled: true — this is the "setup" flow,
//      not the "sign-in challenge" flow LoginForm handles separately.
import { useState } from "react";
import QRCode from "qrcode";
import { authClient } from "../../lib/authClient";

interface TwoFactorSetupProps {
  onComplete: () => void | Promise<void>;
  onSignOut: () => void | Promise<void>;
}

type Step = "password" | "scan" | "confirm" | "backup-codes";

export function TwoFactorSetup({ onComplete, onSignOut }: TwoFactorSetupProps) {
  const [step, setStep] = useState<Step>("password");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [totpURI, setTotpURI] = useState<string | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [code, setCode] = useState("");

  const handleEnable = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const { data, error: enableError } = await authClient.twoFactor.enable({ password });
      if (enableError || !data) {
        setError(enableError?.message || "Couldn't start 2FA setup. Check your password and try again.");
        return;
      }
      const uri = (data as { totpURI: string }).totpURI;
      const codes = (data as { backupCodes?: string[] }).backupCodes || [];
      setTotpURI(uri);
      setBackupCodes(codes);
      const dataUrl = await QRCode.toDataURL(uri);
      setQrDataUrl(dataUrl);
      setStep("scan");
    } catch (err) {
      console.error("2FA enable failed:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const { error: verifyError } = await authClient.twoFactor.verifyTotp({ code });
      if (verifyError) {
        setError(verifyError.message || "That code didn't match. Please try again.");
        return;
      }
      setStep("backup-codes");
    } catch (err) {
      console.error("2FA confirm failed:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-[480px] rounded-lg border border-gray-200 bg-white p-8 shadow-sm">
        <h1 className="mb-1 text-xl font-semibold text-gray-900">Set up two-factor authentication</h1>
        <p className="mb-6 text-sm text-gray-500">
          Your role requires 2FA to be enabled before you can continue.
        </p>

        {step === "password" && (
          <form onSubmit={handleEnable} className="space-y-4">
            <div>
              <label htmlFor="tfa-password" className="mb-1 block text-sm font-medium text-gray-700">
                Confirm your password to continue
              </label>
              <input
                id="tfa-password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? "Starting…" : "Continue"}
            </button>
            <button
              type="button"
              onClick={() => onSignOut()}
              className="w-full text-xs text-gray-500 hover:text-blue-600 hover:underline"
            >
              Sign out instead
            </button>
          </form>
        )}

        {step === "scan" && qrDataUrl && (
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              Scan this QR code with an authenticator app (Google Authenticator, Authy, 1Password, etc.).
            </p>
            <div className="flex justify-center">
              <img src={qrDataUrl} alt="2FA QR code" className="h-48 w-48 rounded border border-gray-200" />
            </div>
            <details className="text-xs text-gray-500">
              <summary className="cursor-pointer hover:text-blue-600">Can't scan? Enter code manually</summary>
              <code className="mt-1 block break-all rounded bg-gray-50 p-2 text-[11px]">{totpURI}</code>
            </details>

            <form onSubmit={handleConfirm} className="space-y-4">
              <div>
                <label htmlFor="tfa-code" className="mb-1 block text-sm font-medium text-gray-700">
                  Enter the 6-digit code from your app
                </label>
                <input
                  id="tfa-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm tracking-widest focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="123456"
                />
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <button
                type="submit"
                disabled={submitting || !code}
                className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? "Verifying…" : "Verify and enable"}
              </button>
            </form>
          </div>
        )}

        {step === "backup-codes" && (
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              2FA is now enabled. Save these backup codes somewhere safe — each can be used once if you lose
              access to your authenticator app.
            </p>
            {/* BUG FIX: these were shown in a 2-column grid, and every real
                backup code from better-auth is itself two 5-character groups
                joined by one dash (e.g. "dQRRm-BbtEK") — so two separate
                codes sitting side by side in the same row read as a single
                4-segment code (e.g. "dQRRm-BbtEK-e971w-xYVXp"), which is
                exactly the wrong value to type in. Single column removes
                that ambiguity entirely. */}
            <div className="space-y-1.5 rounded-md bg-gray-50 p-4 font-mono text-sm">
              {backupCodes.map((c) => (
                <div key={c}>{c}</div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => onComplete()}
              className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
            >
              I've saved my backup codes — continue
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
